/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { map, takeUntil } from 'rxjs/operators';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { LayerPaths } from '@appPlanApplication/LayerPaths';
import { AbstractAreaDrawer } from '@appCanvasCommon/core/AbstractAreaDrawer';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { combineLatest } from 'rxjs';
import { ViewportChangedEvent } from '@appCanvasCommon/interfaces/camera.interface';
import { DestroyOptions } from '@appCanvasCommon/core/DestroyOptions';
import { cssVar } from '@touchplan/css-var';
import { Rectangle } from 'pixi.js-legacy';
import { IActiveColumn } from '../../../../modules/plan/services/active-column.service';
import { ActiveLine } from '../../../../app-data/models/ActiveLine';
import { ActiveSpaceTimelineDrawer } from '@appPlanApplication/timeline/active-space/ActiveSpaceTimelineDrawer';
import { ShadedColumnDrawer } from '@appPlanApplication/timeline/active-space/ShadedColumnDrawer';

const settings = {
	separatorLineColor: cssVar.grey12,
	separatorLineWidth: 1, // in pixels
	nonWorkDayColor: cssVar.grey12,
};
export class ActiveSpaceDrawer extends AbstractAreaDrawer {
	private _cachedColumns: IActiveColumn[] | null;
	private _cachedActiveLine: ActiveLine | null;

	private _timelineDrawer: ActiveSpaceTimelineDrawer = new ActiveSpaceTimelineDrawer(
		this._canvasAngularLand,
		this.parentApplication
	);

	private _shadedColumnDrawer: ShadedColumnDrawer = new ShadedColumnDrawer(
		this._canvasAngularLand,
		this.parentApplication
	);

	/**
	 * ActiveSpaceDrawer handles management of the active space view.
	 * @param _canvasAngularLand
	 * @param parentApplication
	 */
	constructor(private _canvasAngularLand: CanvasAngularLand, parentApplication: AbstractApplication) {
		super(parentApplication, LayerPaths.BACKGROUND_VISUALS_ACTIVE_SPACE_COLUMNS);
		this._container.eventMode = EventMode.Disabled;
		this.setupSubscribers();
	}

	public draw(columns: IActiveColumn[] | null = this._cachedColumns): void {
		if (
			!columns ||
			!columns.length ||
			!this._cachedActiveLine ||
			!this._cachedActiveLine.view.activeLineX === undefined
		) {
			this.hide();
			this._timelineDrawer.draw(null);
			return;
		}
		// Parent draw clears our graphics drawing data and sets the container to visible
		super.draw();

		const cameraView: Rectangle = this.parentApplication.camera.view;

		columns.forEach((column: IActiveColumn) => {
			const screenX: number = this.parentApplication.camera.realToScreenX(column.finishX);
			this._graphics.beginFill(settings.separatorLineColor);
			this._graphics.drawRect(screenX, 0, settings.separatorLineWidth, cameraView.height);
			this._graphics.endFill();
		});

		this._timelineDrawer.draw(columns);
	}

	/**
	 * Updates the position of the container.
	 * @param _ The time since the last frame, not used.
	 */
	public update(delta?: number): void {
		if (!this._cachedActiveLine) {
			return;
		}
		this._timelineDrawer.update(delta);
	}

	/**
	 * Destroy all the things. We need to override the abstract destroy so we can clear up our objects specific to this model
	 * @param options
	 */
	public destroy(options: DestroyOptions = DestroyOptions.includeChildren): void {
		super.destroy(options);
		this._timelineDrawer.destroy(DestroyOptions.sourceOnly);
		this._shadedColumnDrawer.destroy(DestroyOptions.sourceOnly);
		this._canvasAngularLand = null;
	}

	/**
	 * Setup all internal subscriptions that causes this drawer to re-draw
	 * @private
	 */
	private setupSubscribers(): void {
		combineLatest([
			this.parentApplication.camera.changed$,
			this._canvasAngularLand.activeColumnService.visibleColumns$,
			this._canvasAngularLand.activeLineService.changed$,
		])
			.pipe(
				takeUntil(this._shutdownSubject$),
				map(
					([changedEvent, activeColumns, activeLine]: [
						ViewportChangedEvent,
						IActiveColumn[],
						ActiveLine
					]) => {
						this._cachedActiveLine = activeLine;
						this._cachedColumns = activeColumns;
						return activeColumns;
					}
				)
			)
			.subscribe((visibleColumns: IActiveColumn[]) => this.draw(visibleColumns));
	}
}
